import type { ReferenceLoader, ReferenceOption } from '~/components/workbench/fields';
import { apiListMetaobjects, type DataDefinition } from '~/lib/api/data';
import { apiListCollections, apiListProducts } from '~/lib/api/shop';
import { entryTitle } from './content-model';

/**
 * A metaobject reference without a namespace points at a type in the field's
 * own namespace (the server resolves it the same way): qualify it as
 * `namespace.type` so the loader lists the right entries, e.g. a plugin's
 * field pointing at that plugin's own type.
 */
export function qualifyReference<F extends { type: string; validations?: { metaobjectType?: string } }>(
  field: F,
  namespace: string,
): F {
  const ref = field.validations?.metaobjectType;

  if (field.type !== 'metaobject_reference' || !ref || ref.includes('.')) {
    return field;
  }

  return { ...field, validations: { ...field.validations, metaobjectType: `${namespace}.${ref}` } };
}

/**
 * Choices for reference fields in this app: shop products / variants /
 * collections, and entries of the referenced content type. Each list is
 * fetched once per loader (one loader per panel mount). A missing shop
 * answers an empty list, never an error.
 */
export function createReferenceLoader(appId: string, types: () => readonly DataDefinition[]): ReferenceLoader {
  const cache = new Map<string, Promise<ReferenceOption[]>>();

  const once = (key: string, load: () => Promise<ReferenceOption[]>) => {
    if (!cache.has(key)) {
      cache.set(
        key,
        load().catch((err) => {
          cache.delete(key);
          throw err;
        }),
      );
    }

    return cache.get(key)!;
  };

  return (field) => {
    switch (field.type) {
      case 'product_reference':
        return once('product', async () =>
          (await apiListProducts(appId).catch(() => [])).map((p) => ({ value: p.product.id, label: p.product.title })),
        );
      case 'variant_reference':
        return once('variant', async () =>
          (await apiListProducts(appId).catch(() => [])).flatMap((p) =>
            p.variants.map((v) => ({
              value: v.id,
              label: v.options.length ? `${p.product.title} / ${v.options.join(' / ')}` : p.product.title,
            })),
          ),
        );
      case 'collection_reference':
        return once('collection', async () =>
          (await apiListCollections(appId).catch(() => [])).map((c) => ({ value: c.id, label: c.title })),
        );
      case 'metaobject_reference': {
        const ref = field.validations?.metaobjectType ?? '';
        const dot = ref.lastIndexOf('.');
        const namespace = dot > 0 ? ref.slice(0, dot) : 'custom';
        const type = dot > 0 ? ref.slice(dot + 1) : ref;
        const def = types().find((d) => d.kind === 'metaobject' && d.key === type && d.namespace === namespace);

        return once(`mo:${namespace}.${type}`, async () => {
          const page = await apiListMetaobjects(appId, type, { namespace, limit: 100 });
          return page.items.map((e) => ({ value: e.id, label: entryTitle(e, def) }));
        });
      }
      default:
        return Promise.resolve([]);
    }
  };
}
